import { useCallback, useRef, useState } from 'react'
import { Button, Input, Space, Spin, Typography, theme, Tag } from 'antd'
import type { InputRef } from 'antd'
import { SendOutlined, StarOutlined } from '@ant-design/icons'
import { managerChat } from '@/api'
import { createUuid } from '@/utils/uuid'
import type { Msg, ManagerDetail } from '@/types'
import '@/styles/aiChat.scss'

const { Text } = Typography

type Msge = Msg & {
    context?: Partial<ManagerDetail>
}

type ResMsg = Msg & {
    shortcutType?: boolean
    conclusion?: {
        target: string
        value: string
    } | null
}

const shortcutTypeList = ['优化策略','推荐关注板块','优化风控条件']

export default function AIAnalysis({
    type = 'edit',
    formData = {},
    updateFormData = () => {},
}: {
    type?: 'create' | 'edit'
    formData?: Partial<ManagerDetail>
    updateFormData?: (nextFormData: Partial<ManagerDetail> ) => void
}) {
    const { token } = theme.useToken()
    const [sessionId] = useState(() => createUuid())
    const [messages, setMessages] = useState<ResMsg[]>([])
    const [input, setInput] = useState('')
    const [loading, setLoading] = useState(false)
    const chatBodyRef = useRef<HTMLDivElement>(null)
    const inputRef = useRef<InputRef>(null)
    const [time] = useState(new Date().toLocaleTimeString())

    const scrollBottom = () =>
        setTimeout(() => {
            const chatBody = chatBodyRef.current
            if (!chatBody) return
            chatBody.scrollTo({
                top: chatBody.scrollHeight,
                behavior: 'smooth',
            })
        }, 50)

    const send = useCallback(async (msg: string) => {
        if (!msg) return
        const userMsg: Msge = { 
            session_id: sessionId, role:'user',
            message: msg, time: new Date().toLocaleTimeString(),
            context: formData,
        }
        setMessages((prev) => [...prev, userMsg])
        setInput('')
        setLoading(true)
        scrollBottom()
        try {
            const { response , response_html, conclusion } = await managerChat(userMsg)
            setMessages((prev) => [
                ...prev,
                { 
                    session_id: sessionId, 
                    role: 'assistant', 
                    message: response_html || response, 
                    conclusion: conclusion,
                    time: new Date().toLocaleTimeString() ,
                    shortcutType: shortcutTypeList.indexOf(msg) !== -1 ? true : false,
                },
            ])
            scrollBottom()
        } finally {
            setLoading(false)
            setTimeout(() => inputRef.current?.focus(), 0)
        }
    }, [input, loading, formData])

    const handleAnalysis = (msg: string) => {
        send(msg)
        scrollBottom()
    }

    const handleClear = () => {
        !loading && setMessages([])
    }

    const handleShortcut = (label: string) => {
        send(label)
        scrollBottom()
    }

    const sendMsg = () => {
        send(input)
    }

    const handleStrategyWriteBack = (target: string, text: string) => {
        updateFormData({
            ...formData,
            [target]: text || '',
        })
    }

    return (
        <div className='ai-chat-container'>
            {/* Header */}
            <div
                className='ai-chat-header'
            >
                <div className='ai-chat-header-title'><StarOutlined style={{ color: '#3b82f6' }} />组合经理助手<div className='ai-dot'></div></div>
                <Tag style={{ fontSize: 10 , cursor: 'pointer'}} onClick={handleClear}>清空</Tag>
            </div>

            {/* Chat area */}
            <div className='ai-chat-body' ref={chatBodyRef}>

                <div className='ai-message ai-chat-alert-messages'>
                    <div className='ai-chat-alert-messages-title'>
                        正在创建新的组合经理。
                    </div>
                    <div className='ai-chat-alert-messages-content'>
                        点击快捷指令或输入：<br />
                        • **“优化策略”** → AI根据你填写的投资风格生成策略<br />
                        • **“推荐关注板块”** → AI根据你填写的投资内容推荐适合的板块<br />
                        • **“优化风控条件”** → AI根据你填写的投资内容自动生成合理的风控条件<br />
                    </div>
                    <div className='ai-message-time'>{time}</div>
                </div>

                {messages.map((msg,index) => (
                    <div
                        key={index}
                        className={`ai-message ${msg.role === 'user' ? 'ai-message-user' : 'ai-message-assistant'}`}
                    >
                        {
                            msg.role === 'user' ? msg.message : <div dangerouslySetInnerHTML={{ __html: msg.message }} />
                        }
                        {
                             msg.role === 'assistant' && msg.shortcutType && (
                                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button 
                                    type='link' 
                                    className='ai-message-strategy-write-back' 
                                    onClick={() => handleStrategyWriteBack( msg.conclusion?.target || '', msg.conclusion?.value || '')}>采纳</Button>
                                </div>
                            )
                        }
                        <div className='ai-message-time'>{msg.time}</div>
                    </div>
                ))}

                {loading && (
                    <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 12 }}>
                        <div style={{ padding: '8px 12px', background: token.colorFillSecondary, borderRadius: 8 }}>
                            <Spin size="small" />
                        </div>
                    </div>
                )}
                
                <div className='ai-chat-shortcut'>
                    <div className='ai-chat-shortcut-title text-10' style={{display: 'flex', alignItems: 'center', gap: 6}}>
                        表单联动 <Tag style={{ fontSize: 10 }}>直接修改左侧内容</Tag>
                    </div>
                    <div className='ai-chat-shortcut-btn'>
                        {
                            shortcutTypeList.map((item) => (
                                <Text key={item} className='text-12' style={{ marginRight: 6, cursor: 'pointer' }} onClick={() => handleShortcut(item)}>{item}</Text>
                            ))
                        }
                    </div>
                    <div className='ai-chat-shortcut-analysis'>
                        <Text className='text-10' style={{ color: 'var(--text-3)' }}>AI分析：</Text><br />
                        {
                            type ==='create' ?  (
                                <>
                                    
                                    <span className='ai-chat-shortcut-analysis-content' onClick={() => handleAnalysis('分析适合的板块组合')}>
                                    分析适合的板块组合
                                    </span>
                                    <span className='ai-chat-shortcut-analysis-content' style={{ marginLeft: 10 }} onClick={() => handleAnalysis('评估策略可行性')}>
                                    评估策略可行性
                                    </span>
                                </>
                            ): (
                                <>
                                    <span className='ai-chat-shortcut-analysis-content' onClick={() => handleAnalysis(`分析${formData.nickname}的策略优势`)}>
                                    {`分析${formData .nickname}的策略优势`}
                                    </span>
                                    <span className='ai-chat-shortcut-analysis-content' style={{ marginLeft: 10 }} onClick={() => handleAnalysis('评估风险约束是否合理')}>
                                    评估风险约束是否合理
                                    </span>
                                    <span className='ai-chat-shortcut-analysis-content' style={{ marginLeft: 10 }} onClick={() => handleAnalysis('推荐适合的选股方向')}>
                                    推荐适合的选股方向
                                    </span>
                                </>
                            )
                        }
                    </div>
                </div>
            </div>

            {/* Input bar */}
            <div style={{ padding: '15px 20px 20px', borderTop: `1px solid var(--border)` }}>
                <Space.Compact style={{ width: '100%' }}>
                    <Input
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onPressEnter={() => send(input)}
                        placeholder="描述你想要的基金经理风格..."
                        disabled={loading}
                        className='ai-input'
                    />
                    <Button type="primary" disabled={!input} style={{height: 36}} icon={<SendOutlined />} onClick={() => sendMsg()} loading={loading} />
                </Space.Compact>
            </div>
        </div>
    )
}
